import { Product, Additional, Scheduling, SellingMode, AdditionalType } from '@src/core/buyerApiClient';
import type { TariffsPerType } from '@src/stores/product';
import { DefaultMessagesKeys } from '@src/i18n/useTranslate';

interface GetAdditionalAvailabilityResponse {
  availableAmount: number | null;
  isAvailable: boolean;
}

export const getAdditionalAvailability = (
  schedulings: Scheduling[] | undefined,
  additional: Additional,
  product: Product,
  selectedDate: string | undefined,
  selectedMinute: number | undefined,
  listOfTariffsPerType: TariffsPerType[],
): GetAdditionalAvailabilityResponse => {
  let availableAmount = null;

  switch (product.sellingMode) {
    case SellingMode.WithDateAndTime: {
      availableAmount =
        schedulings
          ?.find((scheduling) => scheduling.date === selectedDate)
          ?.occupationRates?.additionalScheduleQuantities?.find(
            ({ additionalId }) => `${additionalId}` === additional.id,
          )
          ?.occupationSchedule?.find(({ minutes }) => minutes === selectedMinute)?.occupation.availableAmount ?? null;

      return checkAvailability(availableAmount, additional, listOfTariffsPerType);
    }

    case SellingMode.WithDateOnly: {
      availableAmount =
        schedulings
          ?.find((scheduling) => scheduling.date === selectedDate)
          ?.occupationRates?.additionalQuantities?.find(({ additionalId }) => `${additionalId}` === additional.id)
          ?.occupation.availableAmount ?? null;

      return checkAvailability(availableAmount, additional, listOfTariffsPerType);
    }

    default:
      return {
        availableAmount: null,
        isAvailable: true,
      };
  }
};

const checkAvailability = (
  availableAmount: number | null,
  additional: Additional,
  listOfTariffsPerType: TariffsPerType[],
) => {
  const isAvailable =
    availableAmount === null ? true : isAdditionalAvailable(availableAmount, additional, listOfTariffsPerType);

  return {
    availableAmount,
    isAvailable,
  };
};

const perPersonTypes: AdditionalType[] = [AdditionalType.PerPerson, AdditionalType.PerPersonWithInput];
export const additionalTypeIsPerson = (add: Additional) => perPersonTypes.includes(add.type);

const isAdditionalAvailable = (
  availableAmount: number,
  additional: Additional,
  listOfTariffsPerType: TariffsPerType[],
) => {
  if (additionalTypeIsPerson(additional)) {
    const selectedTarriffQuantity = listOfTariffsPerType.reduce(
      (total, currernt) => (total += currernt.selectedQuantity),
      0,
    );

    return availableAmount >= selectedTarriffQuantity;
  }

  return availableAmount > 0;
};

interface ValidateQuantityProps {
  quantity?: number;
  availableAmount: number | null;
  additional: Additional;
  i18n?: (messageId: DefaultMessagesKeys | undefined, query?: Record<string, string>) => string;
}

export const validateAdditionalQuantity = ({ quantity, availableAmount, additional, i18n }: ValidateQuantityProps) => {
  if (!quantity) {
    return { isQuantityValid: false, quantityError: undefined };
  }

  const errorMessages = {
    lessThanOne: i18n?.('adder_additionals_custom_type_quantity_error'),
    exceedsAvailable: i18n?.('adder_additionals_custom_type_max_quantity_error', {
      maxQuantity: String(availableAmount),
    }),
    exceedsMaxQuantity: i18n?.('adder_additionals_custom_type_max_quantity_error', {
      maxQuantity: String(additional.maxQuantity),
    }),
  };

  if (quantity < 1) {
    return { isQuantityValid: false, quantityError: errorMessages.lessThanOne };
  }

  if (availableAmount !== null && quantity > availableAmount) {
    return { isQuantityValid: false, quantityError: errorMessages.exceedsAvailable };
  }

  if (additional.maxQuantity && quantity > additional.maxQuantity) {
    return { isQuantityValid: false, quantityError: errorMessages.exceedsMaxQuantity };
  }

  return { isQuantityValid: true, quantityError: undefined };
};
